import { ReactNode } from 'react';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { useReduxState } from 'hooks';
import { getCustomizedNavTitle } from 'lib/helpers';
import { Page, PageContent, PageHeader, PageNav } from 'components/page';
import { shouldShowAccountDeleteRequests } from 'lib/users/';

import type { NavLink } from 'types';

interface Props {
  activeLink?: string;
  children: ReactNode;
  withSidebar?: boolean;
}

const UsersPage = ({ activeLink = '', children, withSidebar }: Props) => {
  const {
    accountDeleteRequests,
    language,
    messages,
    serverConfig: {
      allowAdminToManageDeleteRequests,
      competenciesAllowAdmins,
      competenciesAllowMatrixForAdmins,
      disableAccountDeleteRequests,
      featureFlags,
      navLinksCustomTexts,
      userEnableOtherProfiles
    },
    user
  } = useReduxState();

  const userIsAdmin = user?.roles.includes('admin');
  const userIsSuperadmin = user?.roles.includes('superadmin');
  const navActiveLink = `/users${activeLink}`;

  const showAccountDeleteRequests = shouldShowAccountDeleteRequests({
    accountDeleteRequests,
    serverConfig: {
      allowAdminToManageDeleteRequests,
      disableAccountDeleteRequests
    },
    user
  });

  const navLinks: NavLink[] = [
    {
      href: `/users`,
      icon: 'user' as const,
      title: messages.commonUsers
    },
    ...(showAccountDeleteRequests
      ? [
          {
            href: '/users/delete-requests',
            icon: 'user-slash' as const,
            title: messages.usersAccountDeleteRequests,
            unreadItems: accountDeleteRequests
          }
        ]
      : []),
    ...(!featureFlags.enableCompetencyImprovements &&
    (userIsSuperadmin || (competenciesAllowAdmins && userIsAdmin))
      ? [
          {
            href: `/users/competencies`,
            icon: 'medal' as const,
            title: messages.commonUserCompetencies
          }
        ]
      : []),
    ...(!featureFlags.enableCompetencyImprovements && userIsSuperadmin
      ? [
          {
            href: `/users/competency-profiles`,
            icon: 'address-card' as const,
            title: messages.competencyProfiles
          }
        ]
      : []),
    ...(!featureFlags.enableCompetencyImprovements &&
    (userIsSuperadmin || (competenciesAllowMatrixForAdmins && userIsAdmin))
      ? [
          {
            href: '/users/competency-matrix',
            icon: 'table' as const,
            title: messages.competencyMatrix
          }
        ]
      : [])
  ];

  return (
    <Page activePathname="/users">
      <PageHeader
        icon="user"
        title={getCustomizedNavTitle({
          defaultTitle: messages.commonUsers,
          navLinksCustomTexts,
          language,
          section: 'users'
        })}
        titleDescription={
          user?.roles?.includes('superadmin')
            ? messages.usersPageDescriptionSuperadmin
            : user?.roles?.includes('admin') &&
              user?.permissions?.includes('view-all-users')
            ? messages.usersPageDescriptionAdminViewPermissions
            : user?.roles?.includes('admin')
            ? userEnableOtherProfiles.isEnabled &&
              attributesConditionsMatch({
                conditions: userEnableOtherProfiles.conditions,
                customAttributeValues: user.customAttributes,
                user
              })
              ? messages.usersPageDescriptionAdminLimited
              : messages.usersPageDescriptionAdmin
            : ''
        }
      />

      {navLinks.length > 1 ? (
        <PageNav activeLink={navActiveLink} links={navLinks} />
      ) : null}

      <PageContent withSidebar={withSidebar}>{children}</PageContent>
    </Page>
  );
};

export default UsersPage;
